import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Input, Select, Space, message, Modal } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { dviTemplateApi } from '../../services/api/dviApi';
import { DVITemplateTree, DVITemplatePoint, DVITemplateCategory, DVIPointType, DVIRequirement } from '../../types/dvi';

interface CatNode extends DVITemplateCategory { points: DVITemplatePoint[] }

export interface DVITemplateBuilderProps {
  templateId?: number;
}

// Drag-handle props handed to the card title's grip. Listeners live ONLY on the
// handle — NOT the whole card — so inputs/selects/buttons inside stay editable.
type DragHandleProps = Record<string, unknown>;

const SortableItem: React.FC<{
  id: string;
  children: (handleProps: DragHandleProps) => React.ReactNode;
}> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ ...attributes, ...listeners })}
    </div>
  );
};

export const DVITemplateBuilder: React.FC<DVITemplateBuilderProps> = ({ templateId }) => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [categories, setCategories] = useState<CatNode[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Monotonic counter for temporary (unsaved) row ids. Using a decrementing
  // ref guarantees uniqueness across the component's lifetime — deriving temp
  // ids from array length (the old approach) collided after add/delete cycles.
  const tempIdRef = useRef(-1);
  const nextTempId = () => tempIdRef.current--;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    if (templateId) {
      dviTemplateApi.getById(templateId)
        .then((tree: DVITemplateTree) => {
          setName(tree.name);
          setCategories(tree.categories as CatNode[]);
          setLoaded(true);
        })
        .catch(() => {
          // Surface the failure and exit the loading state so the UI does not
          // stay stuck on "Loading…" forever.
          message.error('Failed to load template');
          setLoaded(true);
        });
    } else {
      setLoaded(true);
    }
  }, [templateId]);

  const handleAddCategory = async () => {
    if (!templateId) {
      // Local-only add until saved
      setCategories(c => [...c, {
        id: nextTempId(), template_id: 0, name: 'New category',
        sort_order: c.length, points: [],
      }]);
      return;
    }
    try {
      const c = await dviTemplateApi.addCategory(templateId, {
        name: 'New category', sortOrder: categories.length,
      });
      setCategories(prev => [...prev, { ...c, points: [] }]);
    } catch {
      message.error('Failed to add category');
    }
  };

  const handleAddPoint = async (catId: number) => {
    if (catId < 0) {
      setCategories(prev => prev.map(c => c.id === catId ? {
        ...c, points: [...c.points, {
          id: nextTempId(), category_id: catId,
          name: 'New point', description: null, sort_order: c.points.length,
          point_type: 'status' as DVIPointType,
          requirement: 'optional' as DVIRequirement,
        }],
      } : c));
      return;
    }
    const cat = categories.find(c => c.id === catId);
    if (!cat) return;
    try {
      const p = await dviTemplateApi.addPoint(catId, {
        name: 'New point', sortOrder: cat.points.length,
      });
      setCategories(prev => prev.map(c => c.id === catId
        ? { ...c, points: [...c.points, p] } : c));
    } catch {
      message.error('Failed to add point');
    }
  };

  const handleDeleteCategory = (catId: number) => {
    Modal.confirm({
      title: 'Delete category? Points inside it will also be removed.',
      onOk: async () => {
        // Optimistically remove, snapshot for rollback on server failure.
        const snapshot = categories;
        setCategories(prev => prev.filter(c => c.id !== catId));
        if (catId > 0) {
          try {
            await dviTemplateApi.deleteCategory(catId);
          } catch {
            message.error('Failed to delete category');
            setCategories(snapshot);
          }
        }
      },
    });
  };

  const handleDeletePoint = (catId: number, pointId: number) => {
    Modal.confirm({
      title: 'Delete point?',
      onOk: async () => {
        const snapshot = categories;
        setCategories(prev => prev.map(c => c.id === catId
          ? { ...c, points: c.points.filter(p => p.id !== pointId) }
          : c));
        if (pointId > 0) {
          try {
            await dviTemplateApi.deletePoint(pointId);
          } catch {
            message.error('Failed to delete point');
            setCategories(snapshot);
          }
        }
      },
    });
  };

  const handleSaveNew = async () => {
    if (saving) return; // in-flight guard against double-submit
    if (!name.trim()) {
      message.error('Template name is required');
      return;
    }
    setSaving(true);
    try {
      await dviTemplateApi.create({
        name,
        categories: categories.map(c => ({
          name: c.name, sortOrder: c.sort_order,
          points: c.points.map(p => ({
            name: p.name, description: p.description ?? undefined, sortOrder: p.sort_order,
            pointType: p.point_type, unit: p.unit, requirement: p.requirement,
          })),
        })),
      });
      message.success('Template created');
      navigate('/owner/settings/dvi-templates');
    } catch {
      message.error('Failed to create template');
      setSaving(false);
    }
  };

  const handleSaveName = async () => {
    if (!templateId) return;
    try {
      await dviTemplateApi.update(templateId, { name });
      message.success('Name saved');
    } catch {
      message.error('Failed to save name');
    }
  };

  const handleUpdateCategoryName = async (catId: number, newName: string) => {
    try {
      await dviTemplateApi.updateCategory(catId, { name: newName });
    } catch {
      message.error('Failed to save category name');
    }
  };

  const handleUpdatePointName = async (pointId: number, newName: string) => {
    try {
      await dviTemplateApi.updatePoint(pointId, { name: newName });
    } catch {
      message.error('Failed to save point name');
    }
  };

  const handleUpdatePointType = async (catId: number, pointId: number, pointType: DVIPointType) => {
    setCategories(prev => prev.map(c => c.id === catId
      ? { ...c, points: c.points.map(p => p.id === pointId ? { ...p, point_type: pointType } : p) }
      : c));
    if (pointId > 0) {
      try {
        await dviTemplateApi.updatePoint(pointId, { pointType });
      } catch {
        message.error('Failed to save point type');
      }
    }
  };

  const handleUpdatePointRequirement = async (catId: number, pointId: number, requirement: DVIRequirement) => {
    setCategories(prev => prev.map(c => c.id === catId
      ? { ...c, points: c.points.map(p => p.id === pointId ? { ...p, requirement } : p) }
      : c));
    if (pointId > 0) {
      try {
        await dviTemplateApi.updatePoint(pointId, { requirement });
      } catch {
        message.error('Failed to save requirement');
      }
    }
  };

  const handleUpdatePointUnit = async (catId: number, pointId: number, unit: string) => {
    setCategories(prev => prev.map(c => c.id === catId
      ? { ...c, points: c.points.map(p => p.id === pointId ? { ...p, unit } : p) }
      : c));
    if (pointId > 0) {
      try {
        await dviTemplateApi.updatePoint(pointId, { unit: unit || null });
      } catch {
        message.error('Failed to save point unit');
      }
    }
  };

  const handleCategoryDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;

    const oldIndex = categories.findIndex(i => `cat-${i.id}` === e.active.id);
    const newIndex = categories.findIndex(i => `cat-${i.id}` === e.over!.id);
    if (oldIndex === -1 || newIndex === -1) return;

    // Compute the reordered array FIRST (no side effects inside the state
    // updater — that runs during render and can double-fire the PUT).
    const reordered = arrayMove(categories, oldIndex, newIndex)
      .map((c, idx) => ({ ...c, sort_order: idx }));
    const snapshot = categories;
    setCategories(reordered);

    // Persist new sort_order outside the updater, with rollback on failure.
    if (templateId) {
      const updates = reordered
        .filter((c, idx) => c.id > 0 && snapshot.find(s => s.id === c.id)?.sort_order !== idx)
        .map(c => dviTemplateApi.updateCategory(c.id, { sortOrder: c.sort_order }));
      if (updates.length > 0) {
        Promise.all(updates).catch(() => {
          message.error('Failed to save category order');
          setCategories(snapshot);
        });
      }
    }
  };

  if (!loaded) return <div style={{ padding: 16 }}>Loading…</div>;

  return (
    <div style={{ padding: 16, maxWidth: 800, margin: '0 auto' }}>
      <Space style={{ marginBottom: 16 }}>
        <Input
          placeholder="Template name"
          value={name}
          onChange={e => setName(e.target.value)}
          onBlur={templateId ? handleSaveName : undefined}
          style={{ width: 300 }}
        />
        {!templateId && (
          <Button type="primary" onClick={handleSaveNew} loading={saving} disabled={saving}>
            Save Template
          </Button>
        )}
      </Space>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryDragEnd}>
        <SortableContext items={categories.map(c => `cat-${c.id}`)} strategy={verticalListSortingStrategy}>
          {categories.map(cat => (
            <SortableItem key={cat.id} id={`cat-${cat.id}`}>
              {(handleProps) => (
              <Card size="small" style={{ marginBottom: 8 }}
                title={
                  <Space style={{ display: 'flex' }}>
                    <span
                      {...handleProps}
                      aria-label="Drag category"
                      style={{ cursor: 'grab', touchAction: 'none', color: '#999', padding: '0 4px' }}
                    >
                      <HolderOutlined />
                    </span>
                    <Input
                      value={cat.name}
                      onChange={e => setCategories(prev => prev.map(c => c.id === cat.id
                        ? { ...c, name: e.target.value } : c))}
                      onBlur={() => { if (cat.id > 0) handleUpdateCategoryName(cat.id, cat.name); }}
                    />
                  </Space>
                }
                extra={
                  <Button danger size="small" onClick={() => handleDeleteCategory(cat.id)}>
                    Delete
                  </Button>
                }
              >
                {cat.points.map(pt => (
                  <Space key={pt.id} style={{ display: 'flex', marginBottom: 4 }} wrap>
                    <Input
                      value={pt.name}
                      onChange={e => setCategories(prev => prev.map(c => c.id === cat.id
                        ? { ...c, points: c.points.map(p =>
                            p.id === pt.id ? { ...p, name: e.target.value } : p) }
                        : c))}
                      onBlur={() => { if (pt.id > 0) handleUpdatePointName(pt.id, pt.name); }}
                    />
                    <Select
                      size="small"
                      value={pt.point_type ?? 'status'}
                      style={{ width: 120 }}
                      aria-label="Point type"
                      onChange={(val: DVIPointType) => handleUpdatePointType(cat.id, pt.id, val)}
                      options={[
                        { value: 'status', label: 'Pass/Fail' },
                        { value: 'photo', label: 'Photo only' },
                        { value: 'reading', label: 'Reading' },
                      ]}
                    />
                    <Select
                      size="small"
                      value={pt.requirement ?? 'optional'}
                      style={{ width: 110 }}
                      aria-label="Requirement"
                      onChange={(val: DVIRequirement) => handleUpdatePointRequirement(cat.id, pt.id, val)}
                      options={[
                        { value: 'optional', label: 'Optional' },
                        { value: 'required', label: 'Required' },
                        { value: 'mandatory', label: 'Mandatory' },
                      ]}
                    />
                    {(pt.point_type ?? 'status') === 'reading' && (
                      <Input
                        size="small"
                        placeholder="unit e.g. mi"
                        value={pt.unit ?? ''}
                        style={{ width: 110 }}
                        aria-label="Unit"
                        onChange={e => setCategories(prev => prev.map(c => c.id === cat.id
                          ? { ...c, points: c.points.map(p =>
                              p.id === pt.id ? { ...p, unit: e.target.value } : p) }
                          : c))}
                        onBlur={() => handleUpdatePointUnit(cat.id, pt.id, pt.unit ?? '')}
                      />
                    )}
                    <Button size="small" danger
                      onClick={() => handleDeletePoint(cat.id, pt.id)}
                    >×</Button>
                  </Space>
                ))}
                <Button size="small" onClick={() => handleAddPoint(cat.id)}>
                  + Add point
                </Button>
              </Card>
              )}
            </SortableItem>
          ))}
        </SortableContext>
      </DndContext>
      <Button onClick={handleAddCategory}>+ Add Category</Button>
    </div>
  );
};

export default DVITemplateBuilder;
